/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ArrowLeft, BookmarkPlus, FileDown } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type GoalStatus } from '../../lib/api/itp.js';
import {
  usePlan,
  useToggleStep,
  useToggleStepItem,
  useUpdateGoalProgress,
} from '../../lib/queries/itp.js';
import { useExportPlanPdf } from '../../lib/queries/reports.js';

import { CollaboratorsSection } from './collaborators-section.js';
import { GoalDetailRow } from './plan-goal-detail.js';
import { ReviewsSection } from './reviews-section.js';
import { SaveTemplateModal } from './save-template-modal.js';

/** Status → roll-up percent so changing a goal's state moves plan progress. */
const STATUS_PCT: Readonly<Record<GoalStatus, number>> = {
  not_started: 0,
  emerging: 25,
  in_progress: 50,
  achieved: 100,
  maintenance: 100,
  discontinued: 0,
};

interface HeaderActionsProps {
  readonly planId: string;
  readonly hasGoals: boolean;
  readonly onSaveTemplate: () => void;
}

/** Export-PDF + Save-as-template buttons for the plan header (BRD M01 #8). */
function PlanHeaderActions({ planId, hasGoals, onSaveTemplate }: HeaderActionsProps): JSX.Element {
  const { t } = useTranslation();
  const exportPdf = useExportPlanPdf();
  const onExport = useCallback(() => {
    exportPdf.mutate(planId);
  }, [exportPdf, planId]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" onClick={onExport} disabled={exportPdf.isPending}>
        <FileDown className="h-4 w-4" aria-hidden="true" />
        {exportPdf.isPending ? t('therapist.plan.exporting') : t('therapist.plan.exportPdf')}
      </Button>
      {hasGoals && (
        <Button variant="outline" onClick={onSaveTemplate}>
          <BookmarkPlus className="h-4 w-4" aria-hidden="true" />
          {t('therapist.plan.saveAsTemplate')}
        </Button>
      )}
    </div>
  );
}

/** Therapist → Therapy Plan Details (Figma). Goals checklist + progress update. */
export function PlanDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const plan = usePlan(id);
  const update = useUpdateGoalProgress(id);
  const toggle = useToggleStep(id);
  const toggleItem = useToggleStepItem(id);

  const onUpdate = useCallback(
    (goalId: string, status: GoalStatus, note: string | undefined) => {
      update.mutate({ goalId, status, progressPct: STATUS_PCT[status], note });
    },
    [update],
  );
  const onToggleStep = useCallback(
    (goalId: string, stepId: string, done: boolean) => {
      toggle.mutate({ goalId, stepId, done });
    },
    [toggle],
  );
  const onToggleItem = useCallback(
    (goalId: string, stepId: string, itemId: string, done: boolean) => {
      toggleItem.mutate({ goalId, stepId, itemId, done });
    },
    [toggleItem],
  );
  const busy = update.isPending || toggle.isPending || toggleItem.isPending;
  const [saveOpen, setSaveOpen] = useState(false);
  const openSave = useCallback(() => {
    setSaveOpen(true);
  }, []);
  const closeSave = useCallback(() => {
    setSaveOpen(false);
  }, []);

  return (
    <div className="mx-auto max-w-3xl p-8">
      <Link
        to="/therapist/plans"
        className="focus-ring mb-4 inline-flex items-center gap-1.5 rounded-lg text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('therapist.plan.back')}
      </Link>

      <QueryState
        isLoading={plan.isLoading}
        isError={plan.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        {plan.data !== undefined && (
          <>
            <PageHeader
              title={plan.data.name}
              subtitle={plan.data.programType ?? undefined}
              action={
                <PlanHeaderActions
                  planId={plan.data.id}
                  hasGoals={plan.data.goals.length > 0}
                  onSaveTemplate={openSave}
                />
              }
            />
            <div className="mb-6">
              <div className="mb-1 flex justify-between text-sm text-slate-500">
                <span>{t('therapist.plans.progress')}</span>
                <span>{plan.data.progressPct}%</span>
              </div>
              <ProgressBar value={plan.data.progressPct} />
            </div>

            <CollaboratorsSection planId={plan.data.id} />

            <ReviewsSection planId={plan.data.id} reviewDate={plan.data.reviewDate} />

            <h2 className="mb-3 text-sm font-semibold text-slate-700">
              {t('therapist.plan.goals')}
            </h2>
            {plan.data.goals.length === 0 ? (
              <p className="text-sm text-slate-500">{t('therapist.plan.noGoals')}</p>
            ) : (
              <div className="space-y-3">
                {plan.data.goals.map((goal) => (
                  <GoalDetailRow
                    key={goal.id}
                    planId={plan.data.id}
                    goal={goal}
                    onUpdate={onUpdate}
                    onToggleStep={onToggleStep}
                    onToggleItem={onToggleItem}
                    disabled={busy}
                  />
                ))}
              </div>
            )}
            <SaveTemplateModal
              open={saveOpen}
              onOpenChange={setSaveOpen}
              defaultName={plan.data.name}
              programType={plan.data.programType}
              goals={plan.data.goals}
              onSaved={closeSave}
            />
          </>
        )}
      </QueryState>
    </div>
  );
}
